import { useEffect, useMemo, useState } from "react";
import { api, getDepartmentFilter, getEffectiveDepartment } from "@/lib/api";
import { useI18n } from "@/i18n/i18n";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatDMY, currentMonthISO, crDate } from "@/lib/dateFormat";
import { GroupsCapacityCard } from "@/components/GroupsCapacityCard";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  TrendingUp, TrendingDown, ArrowRight, Edit3, FileDown, Loader2,
  UserPlus, UserMinus, UserX, UserCheck, Users, RefreshCw, Trash2, PauseCircle,
  ChevronRight,
} from "lucide-react";

const CDN_JSPDF = "https://cdn.jsdelivr.net/npm/jspdf@2.5.1/dist/jspdf.umd.min.js";
const CDN_HTML2CANVAS = "https://cdn.jsdelivr.net/npm/html2canvas@1.4.1/dist/html2canvas.min.js";

const loadScript = (src) => new Promise((resolve, reject) => {
  if (document.querySelector(`script[src="${src}"]`)) return resolve();
  const s = document.createElement("script");
  s.src = src;
  s.onload = resolve;
  s.onerror = () => reject(new Error("Failed to load " + src));
  document.head.appendChild(s);
});

// Financial figures are stored in USD. CRC is the secondary/derived view.
const formatUSD = (n) => `$${Number(n ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const formatCRC = (usdAmount, rate) => `₡${(Number(usdAmount ?? 0) * (rate || 0)).toLocaleString("es-CR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Mes en curso en hora de Costa Rica (nunca la del navegador ni UTC).
const currentMonth = () => currentMonthISO();

const MoneyCell = ({ amount, rate, accent = "" }) => (
  <div>
    <div className={`font-display text-2xl font-bold tracking-tight ${accent}`}>{formatUSD(amount)}</div>
    <div className="text-xs text-slate-500 mt-0.5">≈ {formatCRC(amount, rate)}</div>
  </div>
);

const FlowStep = ({ icon: Icon, label, value, tone = "slate", testId, onClick, count }) => {
  const tones = {
    slate: "bg-slate-100 text-slate-700 border-slate-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    rose: "bg-rose-50 text-rose-700 border-rose-200",
    blue: "bg-blue-50 text-blue-700 border-blue-200",
  };
  const clickable = !!onClick && !!count;
  return (
    <div
      className={`flex-1 min-w-[110px] border rounded-lg p-3 ${tones[tone]} ${clickable ? "cursor-pointer hover:brightness-95 transition-all" : ""}`}
      data-testid={testId}
      onClick={clickable ? onClick : undefined}
    >
      <div className="flex items-center gap-2 mb-1">
        <Icon className="h-4 w-4" />
        <span className="text-[13px] font-medium">{label}</span>
      </div>
      <div className="text-2xl font-bold font-display tracking-tight">{value ?? 0}</div>
      {clickable && <div className="text-[11px] opacity-70 mt-0.5">ver quiénes</div>}
    </div>
  );
};

export default function FinancialOverview() {
  const { t } = useI18n();
  const [month, setMonth] = useState(currentMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [flowList, setFlowList] = useState(null); // { title, rows }
  const [renewalsOpen, setRenewalsOpen] = useState(false);
  const [travelList, setTravelList] = useState(null);
  const [showNotProjected, setShowNotProjected] = useState(false);
  const [prospectsOpen, setProspectsOpen] = useState(false);
  const [programsOpen, setProgramsOpen] = useState(false);
  const [prospectForm, setProspectForm] = useState({
    name: "", amount: "", expected_date: "",
    department_id: getEffectiveDepartment() || "dep_es",
  });
  const [departamentos, setDepartamentos] = useState([]);
  const [overrideInput, setOverrideInput] = useState("");
  const [savingOverride, setSavingOverride] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [rateOpen, setRateOpen] = useState(false);
  const [rateInput, setRateInput] = useState("");
  const [savingRate, setSavingRate] = useState(false);
  const [refreshingRate, setRefreshingRate] = useState(false);
  // Desglose Español / Inglés: solo en la vista consolidada ("Todos").
  const consolidado = getDepartmentFilter() === "";
  const [byDept, setByDept] = useState(null);

  const load = async (m = month) => {
    try {
      setLoading(true);
      const r = await api.get(`/stats/financial?month=${m}`);
      setData(r.data);
    } catch {
      toast.error(t("error_generic"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(month); }, [month]);

  useEffect(() => {
    if (!consolidado) return;
    api.get(`/stats/financial/by-department?month=${month}`)
      .then((r) => setByDept(r.data))
      .catch(() => setByDept(null));
  }, [month, consolidado]);

  const usdRate = data?.currency?.usd_rate || 510;

  const variancePositive = (data?.realized?.variance_abs ?? 0) >= 0;

  const monthLabel = useMemo(() => {
    if (!month) return "";
    const [y, m] = month.split("-");
    const d = new Date(Number(y), Number(m) - 1, 1);
    return d.toLocaleDateString("es-CR", { year: "numeric", month: "long" });
  }, [month]);

  const openOverride = () => {
    setOverrideInput(
      data?.projection?.override != null ? String(data.projection.override) : ""
    );
    setOverrideOpen(true);
  };

  const addProspect = async () => {
    const amount = Number(prospectForm.amount);
    if (!prospectForm.name.trim() || !amount || !prospectForm.expected_date) {
      toast.error("Necesito nombre, monto y fecha estimada");
      return;
    }
    try {
      await api.post("/expected-enrollments", {
        name: prospectForm.name.trim(),
        amount,
        expected_date: prospectForm.expected_date,
        department_id: prospectForm.department_id,
      });
      setProspectForm({ name: "", amount: "", expected_date: `${month}-15` });
      toast.success("Prospecto agregado");
      load(month);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error_generic"));
    }
  };

  const cambiarEscuelaProspecto = async (p) => {
    const otras = (departamentos.length ? departamentos : [{ id: "dep_es" }, { id: "dep_en" }])
      .map((d) => d.id);
    const actual = p.department_id || "dep_es";
    const siguiente = otras[(otras.indexOf(actual) + 1) % otras.length];
    try {
      await api.patch(`/expected-enrollments/${p.id}`, { department_id: siguiente });
      load(month);
    } catch {
      toast.error(t("error_generic"));
    }
  };

  const removeProspect = async (id) => {
    try {
      await api.delete(`/expected-enrollments/${id}`);
      load(month);
    } catch {
      toast.error(t("error_generic"));
    }
  };

  const saveOverride = async () => {
    try {
      setSavingOverride(true);
      const amt = overrideInput.trim() === "" ? null : Number(overrideInput);
      await api.post("/stats/financial/projection-override", { month, amount: amt });
      toast.success(t("saved"));
      setOverrideOpen(false);
      load(month);
    } catch {
      toast.error(t("error_generic"));
    } finally {
      setSavingOverride(false);
    }
  };

  const openRate = () => {
    setRateInput(data?.currency?.usd_rate ? String(data.currency.usd_rate) : "");
    setRateOpen(true);
  };

  const saveRate = async () => {
    try {
      setSavingRate(true);
      const r = rateInput.trim() === "" ? null : Number(rateInput);
      await api.post("/exchange-rate/manual", { rate: r });
      toast.success(t("saved"));
      setRateOpen(false);
      load(month);
    } catch {
      toast.error(t("error_generic"));
    } finally {
      setSavingRate(false);
    }
  };

  const refreshRate = async () => {
    try {
      setRefreshingRate(true);
      await api.post("/exchange-rate/refresh");
      toast.success(t("exchange_rate_refreshed"));
      load(month);
    } catch (e) {
      if (e?.response?.status === 400) {
        toast.error(t("bccr_unavailable"));
      } else {
        toast.error(t("error_generic"));
      }
    } finally {
      setRefreshingRate(false);
    }
  };

  const exportPDF = async () => {
    try {
      setExporting(true);
      await Promise.all([loadScript(CDN_HTML2CANVAS), loadScript(CDN_JSPDF)]);
      const el = document.getElementById("financial-overview-export");
      if (!el) throw new Error("Missing capture element");
      const canvas = await window.html2canvas(el, { scale: 2, backgroundColor: "#ffffff" });
      const imgData = canvas.toDataURL("image/png");
      const { jsPDF } = window.jspdf;
      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = 210;
      const pageHeight = 297;
      const imgWidth = pageWidth - 20;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let y = 10;
      pdf.setFontSize(16);
      pdf.text(`We Speak Spanish · ${t("financial_report")}`, 10, y);
      y += 6;
      pdf.setFontSize(10);
      pdf.text(monthLabel, 10, y);
      y += 4;
      if (imgHeight > pageHeight - 30) {
        // Multi-page if content is taller than a single A4
        let position = 0;
        const ratio = imgHeight / canvas.height;
        while (position < canvas.height) {
          const sliceH = Math.min(canvas.height - position, (pageHeight - 30) / ratio);
          const tmp = document.createElement("canvas");
          tmp.width = canvas.width;
          tmp.height = sliceH;
          tmp.getContext("2d").drawImage(canvas, 0, position, canvas.width, sliceH, 0, 0, canvas.width, sliceH);
          if (position > 0) pdf.addPage();
          pdf.addImage(tmp.toDataURL("image/png"), "PNG", 10, position === 0 ? y : 10, imgWidth, sliceH * ratio);
          position += sliceH;
        }
      } else {
        pdf.addImage(imgData, "PNG", 10, y, imgWidth, imgHeight);
      }
      pdf.save(`reporte-financiero-${month}.pdf`);
    } catch (e) {
      console.error(e);
      toast.error(t("error_generic"));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="mt-10">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <h2 className="text-lg font-display font-semibold text-slate-900">{t("financial_overview")}</h2>
          <p className="text-xs text-slate-500 mt-0.5">{t("financial_overview_hint")}</p>
        </div>
        <div className="flex items-center gap-2">
          <Input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="w-[160px]"
            data-testid="financial-month-input"
          />
          <Button
            variant="outline"
            onClick={exportPDF}
            disabled={exporting || loading}
            data-testid="financial-export-pdf-btn"
          >
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
            {t("export_pdf")}
          </Button>
        </div>
      </div>

      {loading || !data ? (
        <div className="bg-white border border-slate-200 rounded-lg p-10 flex items-center justify-center text-slate-500">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> {t("loading")}
        </div>
      ) : (
        <div id="financial-overview-export" className="space-y-4">
          {/* Exchange rate strip */}
          <div className="flex flex-wrap items-center justify-between gap-2 bg-slate-50 border border-slate-200 rounded-lg px-4 py-2 text-xs">
            <div className="flex items-center gap-2 text-slate-600" data-testid="exchange-rate-display">
              <span className="font-medium">{t("exchange_rate")}:</span>
              <span className="font-display font-semibold text-slate-900">1 USD = ₡{usdRate.toLocaleString("es-CR", { maximumFractionDigits: 2 })}</span>
              <span className="text-slate-400">·</span>
              <span className="uppercase tracking-wider">
                {data.currency.rate_source === "bccr" && "BCCR"}
                {data.currency.rate_source === "manual" && t("manual")}
                {data.currency.rate_source === "fallback" && t("fallback")}
              </span>
              {data.currency.rate_fetched_at && (
                <>
                  <span className="text-slate-400">·</span>
                  <span>{crDate(data.currency.rate_fetched_at)}</span>
                </>
              )}
            </div>
            <div className="flex items-center gap-1">
              <Button size="sm" variant="ghost" onClick={refreshRate} disabled={refreshingRate} data-testid="refresh-rate-btn" title={t("refresh_from_bccr")}>
                {refreshingRate ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
              </Button>
              <Button size="sm" variant="ghost" onClick={openRate} data-testid="edit-rate-btn" title={t("edit_rate")}>
                <Edit3 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>

          {/* Top KPI row: Projection / Ventas / Flujo de caja / Pendiente */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="kpi-projection">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[13px] text-slate-500 font-medium">
                  {t("projection")}
                </span>
                <Button size="sm" variant="ghost" onClick={openOverride} data-testid="edit-projection-btn">
                  <Edit3 className="h-3.5 w-3.5" />
                </Button>
              </div>
              <MoneyCell amount={data.projection.effective} rate={usdRate} accent="text-slate-900" />
              <div className="mt-3 space-y-1 text-xs">
                <button
                  type="button"
                  onClick={() => setRenewalsOpen(true)}
                  className="w-full flex justify-between text-slate-600 hover:text-slate-900"
                  title="Ver quiénes renuevan este mes"
                  data-testid="projection-renewals-btn"
                >
                  <span className="underline decoration-dotted">
                    Renovaciones ({data.projection.expected_renewals ?? 0})
                  </span>
                  <span className="font-semibold text-slate-900">{formatUSD(data.projection.renewals_total ?? 0)}</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setProspectForm((f) => ({ ...f, expected_date: f.expected_date || `${month}-15` }));
                    setProspectsOpen(true);
                  }}
                  className="w-full flex justify-between text-slate-600 hover:text-slate-900"
                  title="Anotar matrículas que espero cerrar"
                  data-testid="projection-prospects-btn"
                >
                  <span className="underline decoration-dotted">
                    Prospectos ({(data.projection.detail?.prospects || []).length})
                  </span>
                  <span className="font-semibold text-slate-900">{formatUSD(data.projection.prospects_total ?? 0)}</span>
                </button>
              </div>
              <div className="text-xs text-slate-500 mt-2">
                {data.projection.override != null
                  ? <>{t("override_active")} · {t("auto")}: {formatUSD(data.projection.auto)}</>
                  : <>{t("auto")} · facturas del mes anterior que deben renovar</>
                }
              </div>
            </div>

            {/* VENTAS DEL MES — la venta cuenta en el mes en que entró la plata
                (paid_date); las facturas sin pagar cuentan por fecha de emisión */}
            <div className="bg-white border-2 border-blue-200 rounded-lg p-5" data-testid="kpi-sales">
              <div className="text-[13px] text-blue-700 font-semibold mb-2">
                Ventas del mes
              </div>
              <MoneyCell amount={data.sales?.total ?? 0} rate={usdRate} accent="text-blue-700" />
              <div className="mt-3 space-y-1 text-xs">
                {Number(data.credit_notes?.total ?? 0) > 0 && (
                  <div className="flex justify-between text-slate-600"
                       title="Notas de crédito emitidas este mes: ya vienen restadas del total de arriba"
                       data-testid="kpi-sales-credit-notes">
                    <span>Notas de crédito</span>
                    <span className="font-semibold text-rose-600">
                      −{formatUSD(data.credit_notes?.total ?? 0)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between text-slate-600">
                  <span>Cobradas</span>
                  <span className="font-semibold text-emerald-600">{formatUSD(data.sales?.paid ?? 0)}</span>
                </div>
                <div className="flex justify-between text-slate-600">
                  <span>Pendientes</span>
                  <span className="font-semibold text-amber-600">{formatUSD(data.sales?.pending ?? 0)}</span>
                </div>
                {Number(data.sales?.live_pass_gross ?? 0) > 0 && (
                  <div className="flex justify-between text-slate-600"
                       title="Pases de WSS Live facturados este mes: ya vienen sumados en el total de arriba"
                       data-testid="kpi-sales-live-pass">
                    <span>Pases WSS Live ({data.sales?.live_pass_count ?? 0})</span>
                    <span className="font-semibold text-teal-600">
                      {formatUSD(data.sales?.live_pass_gross ?? 0)}
                    </span>
                  </div>
                )}
                {Number(data.sales?.scholarship_value ?? 0) > 0 && (
                  <div className="flex justify-between text-slate-600"
                       title="Lo que se regaló en becas este mes, medido al precio del ciclo. No suma a las ventas ni a la caja."
                       data-testid="kpi-sales-scholarship">
                    <span>Becas ({data.sales?.scholarship_students ?? 0})</span>
                    <span className="font-semibold text-violet-600">
                      {formatUSD(data.sales?.scholarship_value ?? 0)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between pt-1 mt-1 border-t border-blue-100">
                  <span className="text-slate-500">% cobrado</span>
                  <span className="font-semibold text-slate-900">
                    {Number(data.sales?.collection_rate ?? 0).toFixed(1)}%
                  </span>
                </div>
              </div>
            </div>

            {/* FLUJO DE CAJA — cash basis: paid_date ∈ mes */}
            <div className="bg-white border-2 border-emerald-200 rounded-lg p-5" data-testid="kpi-cash-flow">
              <div className="text-[13px] text-emerald-700 font-semibold mb-2">
                Flujo de caja
              </div>
              <MoneyCell amount={data.cash_flow?.total ?? 0} rate={usdRate} accent="text-emerald-600" />
              <div className="mt-3 space-y-1 text-xs">
                <div className="flex justify-between text-slate-600" title="Cobros de facturas emitidas este mes">
                  <span>De facturas del mes</span>
                  <span className="font-semibold text-slate-900">{formatUSD(data.cash_flow?.from_current_sales ?? 0)}</span>
                </div>
                <div className="flex justify-between text-slate-600" title="Cobros de facturas generadas en meses anteriores">
                  <span>Recuperación</span>
                  <span className="font-semibold text-slate-900">{formatUSD(data.cash_flow?.from_past_sales ?? 0)}</span>
                </div>
                {Number(data.cash_flow?.refunds ?? 0) > 0 && (
                  <div className="flex justify-between pt-1 mt-1 border-t border-emerald-100"
                       title="Plata devuelta a estudiantes por notas de crédito, en el día en que se entregó"
                       data-testid="kpi-cash-refunds">
                    <span className="text-slate-500">Devoluciones</span>
                    <span className="font-semibold text-rose-600">
                      −{formatUSD(data.cash_flow?.refunds ?? 0)}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* PENDIENTE DE PAGO — cuenta por cobrar: arrastre + del mes sin pagar */}
            <div className="bg-white border-2 border-amber-200 rounded-lg p-5" data-testid="kpi-pending-due">
              <div className="text-[13px] text-amber-700 font-semibold mb-2">
                Pendiente de pago
              </div>
              <MoneyCell amount={data.pending_due?.outstanding_total ?? 0} rate={usdRate} accent="text-amber-600" />
              <div className="mt-3 space-y-1 text-xs">
                <div className="flex justify-between text-slate-600" title="Facturas de meses anteriores que seguían sin pagar">
                  <span>Arrastre de meses anteriores</span>
                  <span className="font-semibold text-slate-900" data-testid="pending-carry-over">{formatUSD(data.pending_due?.carry_over ?? 0)}</span>
                </div>
                <div className="flex justify-between text-slate-600" title="Matrículas y renovaciones de este mes que aún no se pagan">
                  <span>Del mes sin pagar</span>
                  <span className="font-semibold text-slate-900" data-testid="pending-this-month">{formatUSD(data.pending_due?.this_month ?? 0)}</span>
                </div>
              </div>
              <div className="text-xs text-slate-500 mt-3">
                Este total pasa al mes siguiente como arrastre.
              </div>
            </div>
          </div>

          {/* Indicadores secundarios. Las dos primeras fichas van angostas para
              dejarle la mitad de la fila a la capacidad de grupos (06/06/2026). */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className={`bg-white border ${variancePositive ? "border-emerald-200" : "border-rose-200"} rounded-lg px-3.5 py-3`} data-testid="kpi-variance">
              <div className="flex items-center justify-between gap-2">
                <div className="text-[11px] text-slate-500 font-medium truncate">
                  {t("variance")} <span className="text-slate-400">vs. proyección</span>
                </div>
                {variancePositive ? <TrendingUp className="h-3.5 w-3.5 text-emerald-600 shrink-0" /> : <TrendingDown className="h-3.5 w-3.5 text-rose-600 shrink-0" />}
              </div>
              <div className={`font-display text-[22px] font-bold leading-tight tabular-nums mt-0.5 ${variancePositive ? "text-emerald-600" : "text-rose-600"}`}>
                {variancePositive ? "+" : ""}{Number(data.realized.variance_pct || 0).toFixed(2)}%
              </div>
              <div className={`text-[11px] font-semibold ${variancePositive ? "text-emerald-600" : "text-rose-600"}`}>
                {variancePositive ? "+" : ""}{formatUSD(data.realized.variance_abs)}
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-lg px-3.5 py-3" data-testid="kpi-arps">
              <div className="text-[11px] text-slate-500 font-medium truncate">{t("arps")}</div>
              <div className="font-display text-[22px] font-bold leading-tight tabular-nums text-blue-600 mt-0.5">
                {formatUSD(data.arps)}
              </div>
              <div className="text-[11px] text-slate-400" data-testid="arps-hint">
                {formatCRC(data.arps, usdRate)} · ÷ {data.arps_divisor ?? 0} matriculados
                {data.arps_excluded_paused > 0 && (
                  <span className="block">
                    {data.arps_excluded_paused} en pausa todo el mes quedan fuera
                  </span>
                )}
              </div>
            </div>

            <div className="md:col-span-2">
              <GroupsCapacityCard />
            </div>
          </div>

          {/* Español / Inglés por separado, con la suma final (solo en "Todos") */}
          {consolidado && byDept?.departments?.length > 1 && (
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden"
                 data-testid="financial-by-department">
              <div className="px-5 py-3 border-b border-slate-100">
                <h3 className="text-[13px] text-slate-500 font-medium">
                  Por departamento
                </h3>
                <p className="text-[11px] text-slate-400 mt-0.5">
                  Las mismas cifras del mes, separadas por escuela y sumadas al final.
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 text-[12px] text-slate-500">
                      <th className="text-left font-medium px-5 py-2.5">Departamento</th>
                      <th className="text-right font-medium px-3 py-2.5">Proyección</th>
                      <th className="text-right font-medium px-3 py-2.5">Renovaciones</th>
                      <th className="text-right font-medium px-3 py-2.5">Ventas del mes</th>
                      <th className="text-right font-medium px-3 py-2.5">Cobrado</th>
                      <th className="text-right font-medium px-3 py-2.5">Pendiente</th>
                      <th className="text-right font-medium px-3 py-2.5">Caja</th>
                      <th className="text-right font-medium px-5 py-2.5">Matriculados</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byDept.departments.map((d) => (
                      <tr key={d.department_id} className="border-t border-slate-100"
                          data-testid={`financial-dept-row-${d.department_id}`}>
                        <td className="px-5 py-2.5 font-medium text-slate-900">{d.department_name}</td>
                        <td className="px-3 py-2.5 text-right font-mono">{formatUSD(d.projection)}</td>
                        <td className="px-3 py-2.5 text-right font-mono">
                          {formatUSD(d.renewals_total)}
                          <span className="text-slate-400"> ({d.renewals_count})</span>
                        </td>
                        <td className="px-3 py-2.5 text-right font-mono text-blue-700">{formatUSD(d.sales_total)}</td>
                        <td className="px-3 py-2.5 text-right font-mono text-emerald-700">{formatUSD(d.paid)}</td>
                        <td className="px-3 py-2.5 text-right font-mono text-amber-600">{formatUSD(d.pending)}</td>
                        <td className="px-3 py-2.5 text-right font-mono">{formatUSD(d.cash_flow)}</td>
                        <td className="px-5 py-2.5 text-right font-mono">{d.students}</td>
                      </tr>
                    ))}
                    {(byDept.prospects || []).map((p) => (
                      <tr key={p.department_id} className="border-t border-slate-100"
                          data-testid={`financial-prospects-row-${p.department_id}`}>
                        <td className="px-5 py-2.5 text-slate-700">
                          Prospectos · {p.department_name}
                          <span className="text-slate-400"> ({p.count})</span>
                        </td>
                        <td className="px-3 py-2.5 text-right font-mono">{formatUSD(p.amount)}</td>
                        <td className="px-3 py-2.5 text-right font-mono text-slate-300">—</td>
                        <td className="px-3 py-2.5 text-right font-mono text-slate-300">—</td>
                        <td className="px-3 py-2.5 text-right font-mono text-slate-300">—</td>
                        <td className="px-3 py-2.5 text-right font-mono text-slate-300">—</td>
                        <td className="px-5 py-2.5 text-right font-mono text-slate-300">—</td>
                      </tr>
                    ))}
                    <tr className="border-t border-slate-200 bg-slate-50 font-semibold"
                        data-testid="financial-dept-total">
                      <td className="px-5 py-2.5">Total ambos</td>
                      <td className="px-3 py-2.5 text-right font-mono">{formatUSD(byDept.total.projection)}</td>
                      <td className="px-3 py-2.5 text-right font-mono">
                        {formatUSD(byDept.total.renewals_total)}
                        <span className="text-slate-400"> ({byDept.total.renewals_count})</span>
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono text-blue-700">{formatUSD(byDept.total.sales_total)}</td>
                      <td className="px-3 py-2.5 text-right font-mono text-emerald-700">{formatUSD(byDept.total.paid)}</td>
                      <td className="px-3 py-2.5 text-right font-mono text-amber-600">{formatUSD(byDept.total.pending)}</td>
                      <td className="px-3 py-2.5 text-right font-mono">{formatUSD(byDept.total.cash_flow)}</td>
                      <td className="px-5 py-2.5 text-right font-mono">{byDept.total.students}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Student flow waterfall */}
          <div className="bg-white border border-slate-200 rounded-lg p-5">
            <div className="text-[13px] text-slate-500 font-medium mb-3">{t("student_flow")}</div>
            <div className="flex flex-wrap items-center gap-2">
              <FlowStep icon={Users} label={t("start_of_month")} value={data.student_flow.start_of_month} tone="slate" testId="flow-start" />
              <ArrowRight className="h-4 w-4 text-slate-400 hidden sm:block" />
              <FlowStep icon={UserPlus} label={t("new_students")} value={`+${data.student_flow.new}`} tone="emerald" testId="flow-new"
                count={data.student_flow.new}
                onClick={() => setFlowList({ title: t("new_students"), rows: data.student_flow.detail?.new || [] })} />
              <ArrowRight className="h-4 w-4 text-slate-400 hidden sm:block" />
              <FlowStep icon={UserCheck} label={t("reactivated")} value={`+${data.student_flow.reactivated || 0}`} tone="emerald" testId="flow-reactivated"
                count={data.student_flow.reactivated}
                onClick={() => setFlowList({ title: t("reactivated"), rows: data.student_flow.detail?.reactivated || [] })} />
              <ArrowRight className="h-4 w-4 text-slate-400 hidden sm:block" />
              <FlowStep icon={UserMinus} label={t("paused")} value={`-${data.student_flow.paused}`} tone="amber" testId="flow-paused"
                count={data.student_flow.paused}
                onClick={() => setFlowList({ title: t("paused"), rows: data.student_flow.detail?.paused || [] })} />
              <ArrowRight className="h-4 w-4 text-slate-400 hidden sm:block" />
              <FlowStep icon={UserX} label={t("cancelled")} value={`-${data.student_flow.cancelled}`} tone="rose" testId="flow-cancelled"
                count={data.student_flow.cancelled}
                onClick={() => setFlowList({ title: t("cancelled"), rows: data.student_flow.detail?.cancelled || [] })} />
              <ArrowRight className="h-4 w-4 text-slate-400 hidden sm:block" />
              <FlowStep icon={Users} label={t("end_of_month")} value={data.student_flow.end_of_month} tone="blue" testId="flow-end" />
            </div>
            {data.student_flow.travel_paused > 0 && (
              <button
                type="button"
                onClick={() => setTravelList(data.student_flow.detail?.travel_paused || [])}
                className="mt-3 inline-flex items-center gap-2 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-3 py-2 text-xs text-slate-600 hover:bg-slate-100"
                data-testid="flow-travel-paused"
              >
                <PauseCircle className="h-4 w-4 text-slate-500" />
                <span>
                  <strong className="text-slate-800">{data.student_flow.travel_paused}</strong> en pausa por viaje este mes
                </span>
                <span className="text-slate-400">· informativo, no cambia el cuadre · ver quiénes</span>
              </button>
            )}
            <div className="text-xs text-slate-500 mt-3">
              {t("net_change")}: <span className={`font-semibold ${data.student_flow.net_change >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                {data.student_flow.net_change >= 0 ? "+" : ""}{data.student_flow.net_change}
              </span>
            </div>
          </div>

          {/* Ingresos por programa: una sola línea, se despliega al hacer clic */}
          {data.by_program.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden"
                 data-testid="revenue-by-program">
              <button
                data-testid="revenue-by-program-toggle"
                onClick={() => setProgramsOpen((v) => !v)}
                className="w-full flex items-center justify-between gap-4 px-5 py-3.5 text-left
                           hover:bg-slate-50 transition-colors"
              >
                <span className="flex items-center gap-2.5">
                  <ChevronRight className={`h-4 w-4 text-slate-400 transition-transform ${programsOpen ? "rotate-90" : ""}`} />
                  <span className="text-[13px] text-slate-500 font-medium">
                    {t("revenue_by_program")}
                  </span>
                  <span className="text-xs text-slate-400">
                    {data.by_program.length} {t("programs_count")}
                  </span>
                </span>
                <span className="font-mono font-semibold text-slate-900">
                  {formatUSD(data.by_program.reduce((a, r) => a + (r.revenue || 0), 0))}
                </span>
              </button>
              {programsOpen && (
                <table className="w-full text-sm border-t border-slate-100">
                  <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                    <tr>
                      <th className="text-left px-5 py-2 font-medium">{t("program")}</th>
                      <th className="text-right px-5 py-2 font-medium">{t("students")}</th>
                      <th className="text-right px-5 py-2 font-medium">{t("revenue")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.by_program.map((row) => (
                      <tr key={row.name} className="border-t border-slate-100" data-testid={`program-row-${row.name}`}>
                        <td className="px-5 py-3 text-slate-900">{row.name}</td>
                        <td className="px-5 py-3 text-right text-slate-700">{row.students}</td>
                        <td className="px-5 py-3 text-right font-medium text-slate-900">{formatUSD(row.revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      )}

      {/* Quiénes están detrás de cada número del flujo */}
      <Dialog open={!!flowList} onOpenChange={(v) => !v && setFlowList(null)}>
        <DialogContent data-testid="flow-detail-dialog">
          <DialogHeader>
            <DialogTitle>{flowList?.title} · {monthLabel}</DialogTitle>
            <DialogDescription>{(flowList?.rows || []).length} {t("students").toLowerCase()}</DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto divide-y divide-slate-100">
            {(flowList?.rows || []).map((r) => (
              <div key={r.student_id} className="py-2 text-sm text-slate-800" data-testid={`flow-detail-row-${r.student_id}`}>
                {r.name}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Pausados por viaje del mes (informativo) */}
      <Dialog open={!!travelList} onOpenChange={(v) => !v && setTravelList(null)}>
        <DialogContent className="max-w-xl" data-testid="travel-paused-dialog">
          <DialogHeader>
            <DialogTitle>En pausa por viaje · {monthLabel}</DialogTitle>
            <DialogDescription>
              {(travelList || []).length} estudiantes. Siguen matriculados; los que están en pausa
              todo el mes no cuentan en el promedio por estudiante.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto divide-y divide-slate-100">
            {(travelList || []).map((r) => (
              <div key={`${r.student_id}-${r.from_date}`} className="py-2 flex items-center justify-between text-sm"
                   data-testid={`travel-paused-row-${r.student_id}`}>
                <div>
                  <span className="text-slate-800 font-medium">{r.name}</span>
                  <span className="text-slate-500 text-xs ml-2">{r.program_name}</span>
                </div>
                <div className="text-xs text-slate-600">
                  {formatDMY(r.from_date)} → {r.to_date ? formatDMY(r.to_date) : "sin fecha de regreso"}
                  {r.full_month && <span className="ml-2 text-slate-400">(mes completo)</span>}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Renovaciones esperadas del mes */}
      <Dialog open={renewalsOpen} onOpenChange={setRenewalsOpen}>
        <DialogContent className="max-w-2xl" data-testid="renewals-detail-dialog">
          <DialogHeader>
            <DialogTitle>Renovaciones esperadas · {monthLabel}</DialogTitle>
            <DialogDescription>
              {(data?.projection?.detail?.renewals || []).length} programas · {formatUSD(data?.projection?.renewals_total ?? 0)}
              {data?.projection?.renewals_done > 0 && (
                <> · {data.projection.renewals_done} ya renovaron</>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            {(data?.projection?.detail?.not_projected || []).length > 0 && (
              <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
                <button
                  type="button"
                  onClick={() => setShowNotProjected((v) => !v)}
                  className="w-full text-left text-xs font-semibold text-amber-900"
                  data-testid="not-projected-toggle"
                >
                  {data.projection.not_projected_count} programas activos sin renovación proyectada
                  este mes{data.projection.review_needed_count > 0 && (
                    <> · <span className="text-red-700">{data.projection.review_needed_count} para revisar</span></>
                  )} · ver por qué
                </button>
                {showNotProjected && (
                  <div className="mt-2 space-y-1" data-testid="not-projected-list">
                    {data.projection.detail.not_projected.map((r) => (
                      <div key={r.enrollment_id} className="text-xs flex justify-between gap-3"
                           data-testid={`not-projected-row-${r.enrollment_id}`}>
                        <span className="text-slate-800 font-medium whitespace-nowrap">{r.student_name}</span>
                        <span className="text-slate-500 truncate">{r.program_name}</span>
                        <span className={`whitespace-nowrap ${["renovacion_atrasada", "sin_facturas", "factura_excluida"].includes(r.reason) ? "text-red-700" : "text-slate-600"}`}>
                          {r.detail}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            <table className="w-full text-sm">
              <thead className="text-xs text-slate-500 border-b">
                <tr>
                  <th className="text-left py-2">Estudiante</th>
                  <th className="text-left py-2">Programa</th>
                  <th className="text-left py-2">Renueva</th>
                  <th className="text-right py-2">Monto</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(data?.projection?.detail?.renewals || []).map((r, i) => (
                  <tr key={`${r.student_id}-${i}`} data-testid={`renewal-row-${r.student_id}`}>
                    <td className="py-2 text-slate-800">
                      {r.student_name}
                      {r.already_renewed && (
                        <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-100 text-emerald-700"
                              data-testid={`renewal-done-${r.student_id}`}>
                          ya renovó
                        </span>
                      )}
                    </td>
                    <td className="py-2 text-slate-600">{r.program_name}</td>
                    <td className="py-2 text-slate-600">{r.expected_date ? formatDMY(r.expected_date) : "—"}</td>
                    <td className="py-2 text-right font-medium text-slate-900">{formatUSD(r.amount)}</td>
                  </tr>
                ))}
                {!(data?.projection?.detail?.renewals || []).length && (
                  <tr><td colSpan={4} className="py-6 text-center text-slate-500">
                    No hay renovaciones esperadas este mes.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>

      {/* Prospectos esperados: se anotan a mano y suman a la proyección */}
      <Dialog open={prospectsOpen} onOpenChange={(v) => {
        setProspectsOpen(v);
        if (v && departamentos.length === 0) {
          api.get("/departments").then((r) => setDepartamentos(r.data || [])).catch(() => {});
        }
      }}>
        <DialogContent className="max-w-2xl" data-testid="prospects-dialog">
          <DialogHeader>
            <DialogTitle>Prospectos esperados · {monthLabel}</DialogTitle>
            <DialogDescription>
              Matrículas que espera cerrar. Suman a la proyección del mes de la fecha que ponga.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap items-end gap-2 border-b pb-3">
            <div className="flex-1 min-w-[140px]">
              <Label className="text-xs">Nombre</Label>
              <Input value={prospectForm.name} placeholder="Quién / empresa"
                     onChange={(e) => setProspectForm({ ...prospectForm, name: e.target.value })}
                     data-testid="prospect-name-input" />
            </div>
            <div className="w-28">
              <Label className="text-xs">Monto</Label>
              <Input type="number" min="0" value={prospectForm.amount}
                     onChange={(e) => setProspectForm({ ...prospectForm, amount: e.target.value })}
                     data-testid="prospect-amount-input" />
            </div>
            <div className="flex flex-col">
              <Label className="text-xs">Escuela</Label>
              <div className="mt-1 h-10 inline-flex items-center rounded-md border border-slate-200 bg-slate-50 p-1"
                   data-testid="prospect-department-select">
                {(departamentos.length ? departamentos
                  : [{ id: "dep_es", short_name: "Español" }, { id: "dep_en", short_name: "Inglés" }]
                ).map((d) => {
                  const activo = (prospectForm.department_id || "dep_es") === d.id;
                  return (
                    <button
                      key={d.id}
                      type="button"
                      onClick={() => setProspectForm({ ...prospectForm, department_id: d.id })}
                      className={`px-3 h-full rounded text-sm font-medium transition-colors ${
                        activo ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                      data-testid={`prospect-dept-option-${d.id}`}
                    >
                      {d.short_name || d.name}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="w-40">
              <Label className="text-xs">Fecha estimada</Label>
              <Input type="date" value={prospectForm.expected_date}
                     onChange={(e) => setProspectForm({ ...prospectForm, expected_date: e.target.value })}
                     data-testid="prospect-date-input" />
            </div>
            <Button onClick={addProspect} data-testid="prospect-add-btn">Agregar</Button>
          </div>
          <div className="max-h-[45vh] overflow-y-auto divide-y divide-slate-100">
            {(data?.projection?.detail?.prospects || []).map((p) => (
              <div key={p.id} className="py-2 flex items-center justify-between text-sm" data-testid={`prospect-row-${p.id}`}>
                <div>
                  <span className="text-slate-800 font-medium">{p.name}</span>
                  <span className="text-slate-500 text-xs ml-2">{formatDMY(p.expected_date)}</span>
                  <button
                    type="button"
                    title="Cambiar de escuela"
                    onClick={() => cambiarEscuelaProspecto(p)}
                    className={`ml-2 text-[10.5px] font-semibold px-2 py-0.5 rounded-full transition-colors ${
                      (p.department_id || "dep_es") === "dep_en"
                        ? "bg-pink-50 text-pink-700 hover:bg-pink-100"
                        : "bg-blue-50 text-blue-700 hover:bg-blue-100"}`}
                    data-testid={`prospect-dept-${p.id}`}>
                    {(departamentos.find((d) => d.id === (p.department_id || "dep_es")) || {}).short_name
                      || ((p.department_id || "dep_es") === "dep_en" ? "Inglés" : "Español")}
                  </button>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-medium text-slate-900">{formatUSD(p.amount)}</span>
                  <Button variant="ghost" size="sm" className="text-red-600 h-7 px-2"
                          onClick={() => removeProspect(p.id)} data-testid={`prospect-delete-${p.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
            {!(data?.projection?.detail?.prospects || []).length && (
              <p className="py-6 text-center text-slate-500 text-sm">
                Todavía no hay prospectos anotados para este mes.
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Override Dialog */}
      <Dialog open={overrideOpen} onOpenChange={setOverrideOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("override_projection")}</DialogTitle>
            <DialogDescription>{t("override_projection_hint")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-xs text-slate-500 uppercase tracking-wider mb-1 block">{t("amount")} (USD $)</label>
              <Input
                type="number"
                step="0.01"
                value={overrideInput}
                onChange={(e) => setOverrideInput(e.target.value)}
                placeholder={data?.projection?.auto ? Number(data.projection.auto).toFixed(2) : "0.00"}
                data-testid="override-amount-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                {t("leave_empty_to_use_auto")} {data?.projection?.auto ? `(${formatUSD(data.projection.auto)})` : ""}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOverrideOpen(false)}>{t("cancel")}</Button>
            <Button onClick={saveOverride} disabled={savingOverride} className="btn-brand" data-testid="save-override-btn">
              {savingOverride ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {t("save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Exchange Rate Dialog */}
      <Dialog open={rateOpen} onOpenChange={setRateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("edit_exchange_rate")}</DialogTitle>
            <DialogDescription>{t("edit_exchange_rate_hint")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-xs text-slate-500 uppercase tracking-wider mb-1 block">{t("rate")} (1 USD = ₡)</label>
              <Input
                type="number"
                step="0.01"
                value={rateInput}
                onChange={(e) => setRateInput(e.target.value)}
                placeholder="510.00"
                data-testid="rate-input"
              />
              <p className="text-xs text-slate-500 mt-1">{t("leave_empty_for_bccr_auto")}</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRateOpen(false)}>{t("cancel")}</Button>
            <Button onClick={saveRate} disabled={savingRate} className="btn-brand" data-testid="save-rate-btn">
              {savingRate ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {t("save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
